import React from 'react';
import beTwMerge from '@/utils/beTwMerge';

interface MenuItem {
  text: string;
  anchor: string;
}

interface TableOfContentsProps {
  title: string;
  menuItems: MenuItem[];
  backgroundColor?: string;
  textColor?: string;
}

export default function TableOfContents({
  title,
  menuItems,
  backgroundColor = 'tw-bg-color-cream-light',
  textColor = 'tw-text-black',
}: TableOfContentsProps) {
  const frameCss =
    'tw-w-[calc(100% - 32px)] md:tw-w-[85%] tw-mx-4 tw-p-4 md:tw-p-6 md:tw-px-[24px] md:tw-pt-[24px] tw-pb-[32px] tw-gap-[24px] md:tw-mx-auto tw-mb-14 lg:tw-mb-20';
  const headingCss = 'tw-flex tw-items-center tw-gap-[12px] tw-self-stretch';
  const headingText =
    'tw-text-[13px] tw-font-heavy tw-leading-[normal] tw-tracking-[0.65px] tw-uppercase tw-flex-[1_0_auto]';
  const anchorLinkgroup = 'tw-flex tw-items-start tw-gap-[12px] tw-flex-[1_0_0] tw-no-underline hover:tw-no-underline';
  const numberCss =
    'tw-w-[16px] tw-self-stretch tw-text-center tw-no-underline hover:tw-no-underline tw-relative tw-bottom-[1.5px]';
  const typographyCss = 'tw-text-[13px] tw-font-normal tw-leading-[normal] tw-tracking-[0.65px] tw-uppercase';
  const typographyCss2 = 'tw-text-[14px] tw-leading-[normal] tw-underline';
  const anchorLink = 'tw-font-book tw-flex-[1_0_0] tw-text-[14px] tw-leading-[normal] tw-underline';
  const notFocusCss = 'focus:tw-outline-none focus:tw-shadow-none active:tw-outline-none active:tw-shadow-none';
  const LineCss =
    'tw-relative tw-top-1/2 tw-left-0 tw-w-full tw-h-[0.75px] tw-bg-current tw-transform tw--translate-y-1/2';

  const handleScrollToAnchor = (anchor: string) => {
    const cleanAnchor = anchor.startsWith('#') ? anchor.slice(1) : anchor;
    const target = document.getElementById(cleanAnchor);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={`${backgroundColor} ${textColor} ${frameCss}`} style={{ maxWidth: '1172px' }}>
      <div className={beTwMerge('tw-relative tw-mb-2 md:tw-mb-6', headingCss)}>
        <div className={beTwMerge('tw-inline-block tw-relative tw-z-10', headingText)}>{title}</div>
        <div className={LineCss} />
      </div>
      <div className="tw-grid tw-gap-4 tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 gap-x-4">
        {menuItems &&
          menuItems.map((item, index) => (
            <a
              type="button"
              role="button"
              tabIndex={0}
              key={`${index}-${item.text}`}
              onClick={() => handleScrollToAnchor(item.anchor)}
              className={beTwMerge(
                'tw-text-left tw-col-span-1 tw-transition-all tw-cursor-pointer',
                anchorLinkgroup,
                notFocusCss
              )}
            >
              <span
                className={beTwMerge(
                  'tw-text-sm tw-font-semibold tw-text-black hover:tw-text-black',
                  numberCss,
                  typographyCss
                )}
              >
                {String(index + 1).padStart(2, '0')}
              </span>
              <span className={beTwMerge(anchorLink, typographyCss2)}>{item.text}</span>
            </a>
          ))}
      </div>
    </div>
  );
}
